import { Component, OnInit } from '@angular/core';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { routes } from '@app/consts';
import { TranslateService } from '@ngx-translate/core';
import { IntegrationsService } from "@app/modules/site-settings-integrations/services/integrations.service";
import { IntegrationSettings } from "@app/modules/site-settings-integrations/models/integrations.model";
import { IntegrationActionResult } from '../integration-actions/integration-actions.component';

const CONNECTION_TAB_INDEX = 1;

@Component({
    selector: 'app-integration-editor',
    templateUrl: './integration-editor.component.html',
    styleUrls: ['./integration-editor.component.scss']
})
export class IntegrationEditorComponent implements OnInit {

    public integration: string;
    public integrationSettings: IntegrationSettings = null;
    public isLoading: boolean = false;
    public loadFailed: boolean = false;
    public actionResult: IntegrationActionResult = null;
    public initialTabIndex: number = 0;

    constructor(
        private translate: TranslateService,
        private router: Router,
        private route: ActivatedRoute,
        private snackbarService: SnackbarService,
        private integrationService: IntegrationsService,
    ) {
    }

    ngOnInit(): void {
        this.integration = this.getIdInURL();

        if (this.integration !== null) {
            this.getIntegration();
        }
    }

    get isCreateMode(): boolean {
        return this.integration === null;
    }

    // The details endpoint returns the description once per culture ({culture, text}[]) so the
    // general form can edit all of them; the header only wants the current culture's text.
    get description(): string {
        const localizations: { culture: string, text: string }[] = this.integrationSettings?.description;

        if (!Array.isArray(localizations)) {
            return localizations ?? '';
        }

        const current = localizations.find(l => l.culture === this.translate.currentLang)
            ?? localizations.find(l => l.text);

        return current?.text ?? '';
    }

    get integrationInitial(): string {
        const name = this.integrationSettings?.integration?.name;
        return name ? name.charAt(0).toUpperCase() : '?';
    }

    getIdInURL(): string {
        let IdInURL: string;

        this.route.paramMap.subscribe(
            params => IdInURL = params.get('id')
        );

        return IdInURL;
    }

    getIntegration(showLoading: boolean = true) {
        this.isLoading = showLoading;
        this.loadFailed = false;

        this.integrationService.getIntegration(this.integration)
        .subscribe({
            next: res => {
                this.integrationSettings = res;
                this.isLoading = false;
            },
            error: () => {
                this.snackbarService.openSnackBar(this.translate.instant('Integrations-ErrorLoading'), 'clear', 'warn');
                this.isLoading = false;
                this.loadFailed = true;
            }
        });
    }

    // Re-read after a save so the header (status, description) reflects it, without the
    // loading state that would tear down the tabs and any in-progress edits on them.
    onSettingsSaved() {
        this.getIntegration(false);
    }

    onIntegrationCreated(id: string) {
        this.openEditorFor(id);
    }

    navigateBack() {
        this.router.navigate([`${routes.SITE_SETTINGS}/${routes.INTEGRATIONS}`]);
    }

    // Same route (Editor/:id), just a different :id, so Angular reuses this component
    // instance rather than destroying/recreating it - ngOnInit (and its one-shot
    // getIdInURL() read) won't run again, so the id and integrationSettings are refreshed
    // by hand here rather than relying on a route-param subscription.
    private openEditorFor(id: string) {
        this.router.navigate([`${routes.SITE_SETTINGS}${routes.INTEGRATIONS}${routes.EDITOR}/${id}`], {relativeTo: this.route});

        this.integration = id;
        this.initialTabIndex = CONNECTION_TAB_INDEX;
        this.getIntegration();
    }
}
